import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import Announcements from "./Announcements";
import Hero from "./Hero";
import LoginFormModal from "./usermgmt/LoginFormModal";
import CreateAccountUser from "./usermgmt/CreateAccountUser";
import ForgotPassword from "./usermgmt/ForgotPassword";
import ChangePasswordModal from "./usermgmt/ChangePasswordModal";
import PhoneVerificationPopup from "./usermgmt/PhoneVerificationPopup";
import { useUser } from "./context/UserContext";
import { useSubscription } from "./context/SubscriptionContext";
import UserAPI from "./services/UserAPI";
import "./css/Home.css";
import "./css/ContentPanel.css";
// MessageBar removed - selected user now shown in Header

function ContentPanel({
  handleHomeClick,
  sidePanelWidth, // Accept sidePanelWidth as a prop
  isSidePanelMinimized,
  setIsLoginModalOpen,
  setIsCreateAccountModalOpen,
  setIsForgotPasswordModalOpen,
  setIsChangePasswordModalOpen,
  setIsPhoneVerificationPopupOpen,
  isLoginModalOpen,
  isCreateAccountModalOpen,
  isForgotPasswordModalOpen,
  isChangePasswordModalOpen,
  isPhoneVerificationPopupOpen,
  setHeroContent,
  heroContent,
  previousContent,
  goBack,
  navigateWithBack,
  isSessionTimedOut, // New prop to handle session timeout
  smsSuccessfullySent,
  setSmsSuccessfullySent,
}) {
  const {
    selectedUser,
    selectUser,
    userName,
    loggedInUser,
    updateLoggedInUser,
    isReadOnlyView,
  } = useUser(); // Use UserContext
  const { refreshSubscription } = useSubscription();
  const [areAnnouncementsVisible, setAreAnnouncementsVisible] = useState(false);
  const setContentRef = useRef(null); // Ref to access Hero's setContent

  const toggleLoginModal = (isOpen) => {
    setIsLoginModalOpen(isOpen);
  };

  const toggleCreateAccountModal = (isOpen) => {
    setIsCreateAccountModalOpen(isOpen);
  };
  const toggleForgotPasswordModal = (isOpen) => {
    setIsForgotPasswordModalOpen(isOpen);
  };

  const toggleChangePasswordModal = (isOpen) => {
    setIsChangePasswordModalOpen(isOpen);
  };

  const handlePhoneVerificationComplete = async () => {
    console.log("ContentPanel: Phone verification completed");
    // Re-fetch user data to get updated phoneVerified status
    if (loggedInUser?.uuid) {
      try {
        const updatedUser = await UserAPI.getUserByUuid(loggedInUser.uuid);
        if (updatedUser && updatedUser.uuid) {
          console.log(
            "ContentPanel: Updated user data after phone verification:",
            updatedUser,
          );
          updateLoggedInUser(updatedUser);
        }
      } catch (error) {
        console.error(
          "ContentPanel: Failed to refresh user after phone verification:",
          error,
        );
      }
    }

    // Refresh subscription data so useFeatureAccess picks up the account's subscription.
    // During verification flow the subscription fetch may have failed (member user had
    // no user_accounts mapping yet or the earlier fetch timing was off).
    try {
      await refreshSubscription();
      console.log(
        "ContentPanel: Subscription data refreshed after phone verification",
      );
    } catch (error) {
      console.error(
        "ContentPanel: Failed to refresh subscription after phone verification:",
        error,
      );
    }
  };

  const updateHeroContent = (newContent) => {
    if (setContentRef.current) {
      setContentRef.current(newContent); // Update Hero's content
    }
  };
  const onUserSelect = (user) => {
    console.log("User selected:", user); // Debugging line

    // Use the UserContext to manage user selection
    selectUser(user);

    if (user) {
      setHeroContent("Dashboard"); // Update Hero content with selected user
      const userName = `${user.firstName} ${user.lastName}`;
      // Selected user is now shown in the header indicator
    } else {
      // Clear selection when user is null
    }
  };

  const handleAnnouncementsVisibilityChange = (isVisible) => {
    setAreAnnouncementsVisible(isVisible);
  };

  return (
    <div className={`main-content ${isSidePanelMinimized ? "expanded" : ""}`}>
      {!isReadOnlyView && (
        <button
          className={`sidebar-collapse-btn${isSidePanelMinimized ? " minimized" : ""}`}
          onClick={() => window.dispatchEvent(new CustomEvent("toggleSidePanel"))}
          title={isSidePanelMinimized ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="collapse-chevron" />
        </button>
      )}
      {isSessionTimedOut ? (
        // Show minimal content when session has timed out
        <div className="session-timeout-content">
          {/* Empty content area - no Hero, Announcements, or MessageBar */}
          <div className="empty-content-area">
            {/* LoginFormModal will still show over this when isLoginModalOpen is true */}
          </div>
        </div>
      ) : (
        // Normal authenticated content
        <>
          {loggedInUser &&
            loggedInUser.emailVerified &&
            loggedInUser.phoneVerified &&
            !isReadOnlyView && (
              <Announcements
                onVisibilityChange={handleAnnouncementsVisibilityChange}
              />
            )}
          <Hero
            content={heroContent}
            onUserSelect={onUserSelect}
            selectedUser={selectedUser}
            setHeroContent={setHeroContent}
            previousContent={previousContent}
            goBack={goBack}
            navigateWithBack={navigateWithBack}
            isLoginModalOpen={isLoginModalOpen}
            isCreateAccountModalOpen={isCreateAccountModalOpen}
            isForgotPasswordModalOpen={isForgotPasswordModalOpen}
            areAnnouncementsVisible={areAnnouncementsVisible}
          />
        </>
      )}
      {/* Modals - always render these regardless of session timeout state */}
      {isLoginModalOpen && (
        <LoginFormModal
          onClose={() => setIsLoginModalOpen(false)}
          onCreateAccount={() => {
            setIsLoginModalOpen(false);
            setIsCreateAccountModalOpen(true);
          }}
          onForgotPassword={() => {
            setIsLoginModalOpen(false);
            setIsForgotPasswordModalOpen(true);
          }}
          setIsPhoneVerificationPopupOpen={setIsPhoneVerificationPopupOpen} // Pass phone verification popup setter
          setHeroContent={setHeroContent} // Pass setHeroContent to set default page after login
          setSmsSuccessfullySent={setSmsSuccessfullySent} // Pass SMS success setter
        />
      )}
      {isCreateAccountModalOpen && (
        <CreateAccountUser
          onClose={() => setIsCreateAccountModalOpen(false)}
          onGoToLogin={() => {
            setIsCreateAccountModalOpen(false);
            setIsLoginModalOpen(true);
          }}
        />
      )}{" "}
      {isForgotPasswordModalOpen && (
        <ForgotPassword
          onClose={() => setIsForgotPasswordModalOpen(false)}
          onGoToLogin={() => {
            setIsForgotPasswordModalOpen(false);
            setIsLoginModalOpen(true);
          }}
        />
      )}
      {isChangePasswordModalOpen && (
        <ChangePasswordModal
          onClose={() => setIsChangePasswordModalOpen(false)}
        />
      )}
      {isPhoneVerificationPopupOpen && selectedUser && (
        <PhoneVerificationPopup
          user={selectedUser}
          onClose={() => {
            setIsPhoneVerificationPopupOpen(false);
            setSmsSuccessfullySent(false); // Reset for next time
          }}
          onVerificationComplete={handlePhoneVerificationComplete}
          smsSentByLogin={smsSuccessfullySent}
        />
      )}
    </div>
  );
}

export default ContentPanel;
